/**
 * PDF Generator Utility
 * Generates professional PDF documents for waste management plans
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { Plan, PlanData } from '../types';
import { safeJsonParse, isPlainObject } from '@/lib/utils';

/**
 * Create a safe empty PlanData object for use as a fallback
 */
function createEmptyPlanData(): PlanData {
  return {
    project_info: {
      project_name: '',
      project_address: '',
      project_type: 'new_construction',
      estimated_start_date: '',
      estimated_completion_date: '',
    },
    applicant_info: {
      contact_name: '',
      contact_phone: '',
      contact_email: '',
    },
    waste_streams: [],
    compliance: {
      total_waste_generated: 0,
      total_waste_diverted: 0,
      diversion_rate: 0,
      meets_calgreen_requirement: false,
    },
    version: '1.0',
  };
}

/**
 * Format date string to readable format
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Capitalize first letter of each word
 */
function capitalize(str: string): string {
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Generate PDF for a waste management plan
 */
export async function generatePlanPDF(plan: Plan, planUrl: string): Promise<Blob> {
  // Parse plan data
  const planData = safeJsonParse<PlanData>(
    plan.plan_data,
    (value): value is PlanData => isPlainObject(value),
    createEmptyPlanData()
  );

  // Create PDF document (Letter size: 8.5" x 11")
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'in',
    format: 'letter',
  });

  // Define colors
  const primaryColor: [number, number, number] = [37, 99, 235]; // Primary blue
  const textColor: [number, number, number] = [23, 23, 23]; // Near black
  const lightGray: [number, number, number] = [243, 244, 246];

  // Page dimensions
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 0.75;

  // Helper function to add footer
  const addFooter = () => {
    const footerY = pageHeight - 0.5;
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(
      `Generated: ${new Date().toLocaleDateString('en-US')}`,
      margin,
      footerY
    );
    doc.text(`Plan URL: ${planUrl}`, pageWidth - margin, footerY, {
      align: 'right',
    });
  };

  // Header
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 1.2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('CalWMP', margin, 0.6);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.text('California Waste Management Plan', margin, 0.9);

  // Reset text color
  doc.setTextColor(...textColor);

  let yPosition = 1.5;

  // Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Waste Management Plan', margin, yPosition);
  yPosition += 0.3;

  // Plan ID and Status
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(`Plan ID: ${plan.id}`, margin, yPosition);
  doc.text(`Status: ${capitalize(plan.status)}`, pageWidth - margin, yPosition, {
    align: 'right',
  });
  yPosition += 0.5;

  // Section 1: Project Information
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('Project Information', margin, yPosition);
  yPosition += 0.05;

  // Divider line
  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(0.02);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 0.25;

  // Project details
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...textColor);

  const projectInfo = [
    ['Project Name:', planData.project_info.project_name],
    ['Address:', planData.project_info.project_address],
    ['Project Type:', capitalize(planData.project_info.project_type)],
    ['Start Date:', formatDate(planData.project_info.estimated_start_date)],
    [
      'Completion Date:',
      formatDate(planData.project_info.estimated_completion_date),
    ],
  ];

  if (planData.project_info.total_square_footage) {
    projectInfo.push([
      'Square Footage:',
      planData.project_info.total_square_footage.toLocaleString() + ' sq ft',
    ]);
  }

  projectInfo.forEach((item) => {
    const [label, value] = item;
    const displayLabel = label ?? '';
    const displayValue = value ?? '';
    if (displayLabel && displayValue) {
      doc.setFont('helvetica', 'bold');
      doc.text(displayLabel, margin, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text(displayValue, margin + 1.8, yPosition);
      yPosition += 0.2;
    }
  });

  yPosition += 0.2;

  // Section 2: Applicant Information
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('Applicant Information', margin, yPosition);
  yPosition += 0.05;

  doc.setDrawColor(...primaryColor);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 0.25;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...textColor);

  const applicantInfo = [
    ['Contact Name:', planData.applicant_info.contact_name],
    ['Email:', planData.applicant_info.contact_email],
    ['Phone:', planData.applicant_info.contact_phone],
  ];

  if (planData.applicant_info.company_name) {
    applicantInfo.unshift(['Company:', planData.applicant_info.company_name]);
  }

  if (planData.applicant_info.license_number) {
    applicantInfo.push(['License #:', planData.applicant_info.license_number]);
  }

  applicantInfo.forEach((item) => {
    const [label, value] = item;
    const displayLabel = label ?? '';
    const displayValue = value ?? '';
    if (displayLabel && displayValue) {
      doc.setFont('helvetica', 'bold');
      doc.text(displayLabel, margin, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text(displayValue, margin + 1.8, yPosition);
      yPosition += 0.2;
    }
  });

  yPosition += 0.2;

  // Check if we need a new page
  if (yPosition > pageHeight - 3) {
    addFooter();
    doc.addPage();
    yPosition = margin;
  }

  // Section 3: Waste Streams
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('Waste Streams', margin, yPosition);
  yPosition += 0.05;

  doc.setDrawColor(...primaryColor);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 0.15;

  // Waste streams table
  const wasteStreamRows = planData.waste_streams.map((stream) => [
    capitalize(stream.material_type),
    stream.estimated_quantity.toFixed(2) + ' tons',
    capitalize(stream.diversion_method),
    stream.facility_name || '-',
    stream.facility_address || '-',
  ]);

  autoTable(doc, {
    startY: yPosition,
    head: [['Material Type', 'Quantity', 'Diversion Method', 'Facility', 'Address']],
    body: wasteStreamRows,
    theme: 'grid',
    headStyles: {
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontSize: 9,
      fontStyle: 'bold',
      halign: 'left',
    },
    bodyStyles: {
      fontSize: 9,
      textColor: textColor,
    },
    alternateRowStyles: {
      fillColor: lightGray,
    },
    margin: { left: margin, right: margin },
    columnStyles: {
      0: { cellWidth: 1.3 },
      1: { cellWidth: 1.0 },
      2: { cellWidth: 1.3 },
      3: { cellWidth: 1.5 },
      4: { cellWidth: 1.9 },
    },
  });

  // Get the final Y position after the table
  yPosition = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 0.3;

  // Check if we need a new page for compliance section
  if (yPosition > pageHeight - 2.5) {
    addFooter();
    doc.addPage();
    yPosition = margin;
  }

  // Section 4: Compliance Summary
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('CalGreen 5.408 Compliance Summary', margin, yPosition);
  yPosition += 0.05;

  doc.setDrawColor(...primaryColor);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 0.25;

  // Compliance box
  const boxWidth = pageWidth - 2 * margin;
  const boxHeight = 1.2;
  const boxColor: [number, number, number] = planData.compliance.meets_calgreen_requirement
    ? [220, 252, 231] // Green background
    : [254, 226, 226]; // Red background

  doc.setFillColor(...boxColor);
  doc.roundedRect(margin, yPosition, boxWidth, boxHeight, 0.1, 0.1, 'F');

  yPosition += 0.3;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...textColor);

  const complianceStats = [
    ['Total Waste Generated:', `${planData.compliance.total_waste_generated.toFixed(2)} tons`],
    ['Total Waste Diverted:', `${planData.compliance.total_waste_diverted.toFixed(2)} tons`],
    ['Diversion Rate:', `${planData.compliance.diversion_rate.toFixed(1)}%`],
  ];

  complianceStats.forEach((item) => {
    const [label, value] = item;
    const displayLabel = label ?? '';
    const displayValue = value ?? '';
    if (displayLabel && displayValue) {
      doc.setFont('helvetica', 'bold');
      doc.text(displayLabel, margin + 0.2, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text(displayValue, margin + 2.5, yPosition);
      yPosition += 0.25;
    }
  });

  yPosition += 0.05;

  // Compliance status
  const statusText = planData.compliance.meets_calgreen_requirement
    ? 'MEETS CalGreen 5.408 Requirement (≥ 65% diversion)'
    : 'DOES NOT MEET CalGreen 5.408 Requirement (< 65% diversion)';

  const statusColor: [number, number, number] = planData.compliance.meets_calgreen_requirement
    ? [22, 163, 74] // Green
    : [220, 38, 38]; // Red

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...statusColor);
  doc.text(statusText, margin + 0.2, yPosition);
  yPosition += 0.4;

  // Notes section if present
  if (planData.notes && planData.notes.trim()) {
    yPosition += 0.2;

    // Check if we need a new page
    if (yPosition > pageHeight - 2) {
      addFooter();
      doc.addPage();
      yPosition = margin;
    }

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor);
    doc.text('Additional Notes', margin, yPosition);
    yPosition += 0.05;

    doc.setDrawColor(...primaryColor);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 0.25;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...textColor);

    // Split notes into lines that fit the page width
    const maxWidth = pageWidth - 2 * margin;
    const noteLines = doc.splitTextToSize(planData.notes, maxWidth);

    noteLines.forEach((line: string) => {
      // Check if we need a new page
      if (yPosition > pageHeight - 1) {
        addFooter();
        doc.addPage();
        yPosition = margin;
      }
      if (line) {
        doc.text(line, margin, yPosition);
      }
      yPosition += 0.2;
    });
  }

  // Add footer to the last page
  addFooter();

  // Return PDF as blob
  return doc.output('blob');
}

/**
 * Trigger browser download of PDF
 */
export function downloadPDF(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
